/**
 * StudentLiveDevices — « Choisis tes appareils », ouvert par les chevrons du micro et de la caméra
 * (le web) : la fenêtre du design system (`<LiveDevicesSheet>`), les appareils de la salle
 * (`useRoomDevices`), le niveau du micro et les bruits de fond.
 *
 * Monté seulement quand la fenêtre est ouverte : le niveau du micro ouvre sa propre capture, comme
 * l'ancien indicateur (`MicrophoneLevelIndicatorM`), et elle ne doit pas courir le reste du live. Il
 * écoute le micro choisi, pas celui du système. Le filtre des bruits de fond, lui, vit le temps du
 * live (`useNoiseFilter`, tenu par l'écran) : la fenêtre ne fait que le montrer.
 */
import React from "react"

import { LiveDevicesSheet } from "../../../../../../design-system/student"
import { type UILiveDeviceTab } from "../../../../../../design-system/templates/LiveDevicesSheet"
import { type NoiseFilter, type RoomDevices } from "../../../../../../engine/live/room/roomDevices"
import { useMicrophoneLevel } from "../../../../../../engine/precall/useMicrophoneLevel"
import { liveDeviceItems } from "../../../../view-models/student/Live/liveDevicesViewModel"

type Props = {
    devices: RoomDevices
    tab: UILiveDeviceTab
    onChangeTab: (tab: UILiveDeviceTab) => void
    /** Un appareil choisi : la fenêtre décide si elle se referme. */
    onSelected: (tab: UILiveDeviceTab) => void
    onClose: () => void
    /**
     * Le niveau du micro peut se mesurer : faux quand il est refusé à l'app, ou tenu jusqu'au geste de
     * l'élève (`micGate`) — la mesure le demanderait au navigateur à côté de « Active ton micro ».
     */
    micMeasurable: boolean
    focused: boolean
    cameraPreview?: React.ReactNode
    onOpenHelp: () => void
    /** Le filtre des bruits de fond du live, `null` quand le navigateur ne le permet pas. */
    noise: NoiseFilter | null
}

export function StudentLiveDevices(props: Props) {
    const { devices, tab, onChangeTab, onSelected, onClose, micMeasurable, focused, cameraPreview, onOpenHelp, noise } =
        props
    const listening = tab === "microphone" && micMeasurable
    const micLevel = useMicrophoneLevel(
        listening ? { microphone: true } : null,
        devices.microphone.activeId ?? undefined
    ) as number

    const choice = (kind: "microphone" | "camera" | "speaker", noun: string) => ({
        devices: liveDeviceItems(devices[kind].devices, noun),
        activeId: devices[kind].activeId,
        onSelect: (id: string) => {
            devices[kind].select(id)
            onSelected(kind)
        },
    })

    return (
        <LiveDevicesSheet
            visible
            onClose={onClose}
            tab={tab}
            onChangeTab={onChangeTab}
            microphone={choice("microphone", "Micro")}
            speaker={choice("speaker", "Haut-parleur")}
            camera={choice("camera", "Caméra")}
            micLevel={micLevel}
            noiseFilter={
                noise != null
                    ? { enabled: noise.enabled, pending: noise.pending, onChange: noise.setEnabled }
                    : undefined
            }
            cameraPreview={cameraPreview}
            focused={focused}
            onOpenHelp={onOpenHelp}
        />
    )
}
